import {
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type SVGProps,
} from "react";
import { navigate, type Route } from "./router";
import {
  IconActivity,
  IconAligner,
  IconBolt,
  IconCalendar,
  IconCamera,
  IconClipboard,
  IconDocument,
  IconFolder,
  IconHeart,
  IconHelp,
  IconHouse,
  IconGauges,
  IconImages,
  IconInfo,
  IconLigam,
  IconMessage,
  IconMic,
  IconSearchLarge,
  IconSignature,
  IconUserSilhouette,
  IconWallet,
} from "./icons";

type IconCmp = ComponentType<SVGProps<SVGSVGElement>>;

type Tab = { name: Route["name"]; label: string; Icon: IconCmp };

const mainTabs: Tab[] = [
  { name: "summary", label: "Résumé", Icon: IconGauges },
  { name: "cabinet", label: "Cabinet", Icon: IconHouse },
  { name: "messages", label: "Messages", Icon: IconMessage },
];

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult:
    | ((e: { results: { 0: { 0: { transcript: string } } } }) => void)
    | null;
  onerror: ((e: unknown) => void) | null;
  onend: (() => void) | null;
};

function getSpeechRecognition(): (new () => SpeechRecognitionLike) | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function BottomNav({ active }: { active: Route["name"] }) {
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState("");
  const [listening, setListening] = useState(false);
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      // Hauteur du clavier = différence entre la fenêtre et la zone visible.
      const offset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardOffset(offset);
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  useEffect(
    () => () => {
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    },
    [],
  );

  const stopListening = () => {
    recognitionRef.current?.abort();
    recognitionRef.current = null;
    setListening(false);
  };

  const exitSearch = () => {
    stopListening();
    setSearching(false);
    setQuery("");
  };

  const openSearch = () => {
    setSearching(true);
    navigate("search");
  };

  const toggleMic = () => {
    if (listening) {
      stopListening();
      return;
    }
    const Ctor = getSpeechRecognition();
    if (!Ctor) {
      // eslint-disable-next-line no-alert
      alert(
        "Reconnaissance vocale non supportée sur ce navigateur (essaye Chrome ou Safari sur mobile).",
      );
      return;
    }
    const recognition = new Ctor();
    recognition.lang = "fr-FR";
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setQuery(transcript);
    };
    recognition.onerror = () => {
      setListening(false);
      recognitionRef.current = null;
    };
    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
    };
    recognitionRef.current = recognition;
    setListening(true);
    try {
      recognition.start();
    } catch {
      setListening(false);
      recognitionRef.current = null;
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate("search", { q: query.trim() });
  };

  useEffect(() => {
    if (searching) {
      navigate("search", { q: query.trim() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <nav
      aria-label="Navigation principale"
      className="fixed inset-x-0 bottom-0 z-30 transition-[transform] duration-150 ease-out lg:hidden"
      style={
        keyboardOffset > 0
          ? {
              transform: `translateY(-${keyboardOffset}px)`,
              transition: "transform 0ms",
            }
          : undefined
      }
    >
      <div
        ref={wrapRef}
        className="mx-auto flex max-w-md items-stretch gap-1.5 px-4 pb-[max(env(safe-area-inset-bottom)-0.5rem,0.25rem)] pt-0 sm:max-w-lg sm:px-6"
      >
        {searching ? (
          <>
            <button
              type="button"
              onClick={() => {
                exitSearch();
                navigate("summary");
              }}
              aria-label="Résumé"
              className="flex size-14 shrink-0 items-center justify-center rounded-full bg-[var(--overlay-frost)] text-[var(--sf-primary)] shadow-[0_2px_12px_rgba(15,23,42,0.07)] ring-1 ring-[var(--border-1)] backdrop-blur-xl transition active:scale-95"
            >
              <IconGauges width={22} height={22} strokeWidth={2.2} />
            </button>
            <form
              onSubmit={onSubmit}
              className="flex h-14 flex-1 items-center gap-2 rounded-full bg-[var(--overlay-frost)] px-4 shadow-[0_2px_12px_rgba(15,23,42,0.07)] ring-1 ring-[var(--border-1)] backdrop-blur-xl"
            >
              <IconSearchLarge
                width={20}
                height={20}
                strokeWidth={2.2}
                className="shrink-0 text-[var(--text-3)]"
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={listening ? "À l'écoute…" : "Recherche"}
                enterKeyHint="search"
                inputMode="search"
                className="min-w-0 flex-1 bg-transparent text-[16px] text-[var(--text-1)] outline-none placeholder:text-[var(--text-3)]"
              />
              <button
                type="button"
                onClick={toggleMic}
                aria-label={listening ? "Arrêter la dictée" : "Dictée vocale"}
                aria-pressed={listening}
                className={`relative inline-flex size-9 shrink-0 items-center justify-center rounded-full transition active:scale-90 ${
                  listening
                    ? "bg-[var(--sf-primary)] text-white"
                    : "text-[var(--text-2)]"
                }`}
              >
                <IconMic width={20} height={20} />
                {listening ? (
                  <span
                    aria-hidden
                    className="absolute inset-0 animate-ping rounded-full bg-[var(--sf-primary)]/40"
                  />
                ) : null}
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="flex h-14 flex-1 items-stretch justify-around rounded-full bg-[var(--overlay-frost)] px-1 shadow-[0_2px_12px_rgba(15,23,42,0.07)] ring-1 ring-[var(--border-1)] backdrop-blur-xl">
              {mainTabs.map(({ name, label, Icon }) => {
                const isActive = active === name;
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => navigate(name)}
                    aria-current={isActive ? "page" : undefined}
                    className={`relative my-1 flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-full px-2 text-[10.5px] font-semibold transition ${isActive ? "bg-[var(--surface-2)] text-[var(--sf-primary)]" : "text-[var(--text-3)]"}`}
                  >
                    <Icon
                      width={22}
                      height={22}
                      strokeWidth={isActive ? 2.2 : 1.7}
                    />
                    <span className="truncate">{label}</span>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={openSearch}
              aria-label="Rechercher"
              className="flex size-14 shrink-0 items-center justify-center rounded-full bg-[var(--overlay-frost)] text-[var(--text-2)] shadow-[0_2px_12px_rgba(15,23,42,0.07)] ring-1 ring-[var(--border-1)] backdrop-blur-xl transition active:scale-95"
            >
              <IconSearchLarge width={22} height={22} />
            </button>
          </>
        )}
      </div>
    </nav>
  );
}

type SidebarGroup = {
  title?: string;
  items: { name: Route["name"]; label: string; Icon: IconCmp }[];
};

const sidebarGroups: SidebarGroup[] = [
  {
    items: [
      { name: "search", label: "Rechercher", Icon: IconSearchLarge },
      { name: "summary", label: "Résumé", Icon: IconGauges },
      { name: "cabinet", label: "Cabinet", Icon: IconHouse },
      { name: "messages", label: "Messages", Icon: IconMessage },
    ],
  },
  {
    title: "À traiter",
    items: [
      { name: "appointments", label: "Rendez-vous", Icon: IconCalendar },
      { name: "signatures", label: "Signatures", Icon: IconSignature },
      { name: "documents", label: "Documents", Icon: IconFolder },
      { name: "exercises", label: "Exercices", Icon: IconActivity },
      { name: "photo-sessions", label: "Séances photos", Icon: IconCamera },
    ],
  },
  {
    title: "Suivi",
    items: [
      { name: "aligners", label: "Aligneurs", Icon: IconAligner },
      { name: "payments", label: "Paiements", Icon: IconWallet },
      { name: "photos", label: "Photos", Icon: IconImages },
      { name: "astuces", label: "Astuces", Icon: IconInfo },
    ],
  },
  {
    title: "Aide",
    items: [
      { name: "contact", label: "Contact", Icon: IconHelp },
      { name: "emergency", label: "Urgence", Icon: IconBolt },
    ],
  },
];

export function DesktopSidebar({ active }: { active: Route["name"] }) {
  return (
    <aside
      className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-[var(--border-1)] bg-[var(--surface-1)] lg:flex"
      style={{
        paddingTop:
          "max(env(titlebar-area-height, 0px), env(safe-area-inset-top, 0px), 1rem)",
      }}
    >
      <div className="flex items-center gap-2.5 px-4 pb-4">
        <IconLigam width={28} height={28} />
        <div className="min-w-0">
          <p className="text-[15px] font-extrabold leading-tight tracking-tight text-[var(--text-1)]">
            Ligam
          </p>
          <p className="text-[11px] font-medium text-[var(--text-3)]">
            Espace patient
          </p>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-3">
        {sidebarGroups.map((group, gi) => (
          <div key={gi} className={gi === 0 ? "" : "mt-5"}>
            {group.title ? (
              <p className="mb-1.5 px-3 text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--text-3)]">
                {group.title}
              </p>
            ) : null}
            <ul className="flex flex-col gap-0.5">
              {group.items.map(({ name, label, Icon }) => {
                const isActive = active === name;
                return (
                  <li key={`${gi}-${name}`}>
                    <button
                      type="button"
                      onClick={() => navigate(name)}
                      aria-current={isActive ? "page" : undefined}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-1.5 text-[14px] font-semibold transition ${
                        isActive
                          ? "bg-[var(--sf-primary-100)] text-[var(--sf-primary)]"
                          : "text-[var(--text-2)] hover:bg-[var(--surface-2)]"
                      }`}
                    >
                      <Icon
                        width={18}
                        height={18}
                        strokeWidth={isActive ? 2.2 : 1.7}
                      />
                      <span className="truncate">{label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <SidebarFooter />
    </aside>
  );
}

function SidebarFooter() {
  return (
    <div
      className="flex items-center gap-2.5 border-t border-[var(--border-1)] px-3 py-3"
      style={{
        paddingBottom: "max(env(safe-area-inset-bottom, 0px), 0.75rem)",
      }}
    >
      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-[var(--sf-primary-100)] text-[12.5px] font-bold text-[var(--sf-primary)]">
        LM
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-bold text-[var(--text-1)]">
          Laure Martin
        </p>
        <p className="truncate text-[11px] text-[var(--text-3)]">Maman</p>
      </div>
      <button
        type="button"
        aria-label="Profil"
        className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-[var(--text-3)] transition hover:bg-[var(--surface-2)]"
      >
        <IconUserSilhouette width={16} height={16} />
      </button>
    </div>
  );
}
